import React, { useState, useEffect, useRef } from 'react';
import { InlineWebviewProps } from '@dreamai/types';
import { Comments } from '../Comments';
import { useComments } from '../../../../contexts/CommentsContext';
import { useAuth0 } from '@auth0/auth0-react';
import Heading from './Heading';
import api from '../../../../api';

import './inline-webview.scss';

interface ExtractedContent {
  title: string;
  author?: string;
  date?: string;
  html: string;
  text: string;
  url: string;
  siteName?: string;
  images?: string[];
  tags?: string[];
}

interface WebviewData {
  type: 'iframe' | 'screenshot';
  url: string;
  canUseIframe: boolean;
  screenshotUrl?: string;
  reason?: string;
}

/**
 * InlineWebview component for our DSL
 * Displays extracted web content in a scrollable div using Diffbot
 */
const InlineWebview: React.FC<
  InlineWebviewProps & { componentId?: string }
> = ({ url, title, componentId, ...props }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { comments } = useComments();
  const { getAccessTokenSilently } = useAuth0();
  const [webviewData, setWebviewData] = useState<WebviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Get comments for this component
  const componentComments = componentId
    ? comments.componentComments[componentId] || []
    : [];

  useEffect(() => {
    const fetchWebviewData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();

        const response = await api.get(
          `/web-proxy/extract?url=${encodeURIComponent(url)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.status === 202) {
          // Screenshot is being processed
          const result = response.data;
          if (result.processing) {
            setIsProcessing(true);
            setError('Screenshot is being generated, please wait...');

            // Retry after a delay if we haven't exceeded retry limit
            if (retryCount < 5) {
              setTimeout(
                () => {
                  setRetryCount((prev) => prev + 1);
                  fetchWebviewData();
                },
                3000 + retryCount * 1000,
              ); // Increasing delay
            } else {
              setError('Screenshot generation is taking longer than expected');
              setIsProcessing(false);
            }
            return;
          }
        }

        const result = response.data;

        if (result.success) {
          setWebviewData(result.data);
          setIsProcessing(false);
          setRetryCount(0);
        } else {
          setError(result.error || 'Failed to load content');
          setIsProcessing(false);
        }
      } catch (err: any) {
        console.error('Error fetching webview data:', err);
        setError(err.message || 'Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchWebviewData();
    }
  }, [url, getAccessTokenSilently]);

  return (
    <div
      ref={containerRef}
      className='inline-webview-container'
      style={{ position: 'relative' }}
      {...props}
    >
      {/* Title heading with website icon */}
      <Heading level='3' value={title} icon='website' />

      {loading ? (
        <div className='iframe-wrapper'>
          <div className='fake-url-bar'>
            <span className='url-text'>Loading...</span>
          </div>
          <div className='webview-content'>
            <div className='loading-message'>Loading content...</div>
          </div>
        </div>
      ) : error ? (
        <div className='iframe-wrapper'>
          <div className='fake-url-bar'>
            <span className='url-text'>{url}</span>
          </div>
          <div className='webview-content'>
            <div className='error-message'>Error: {error}</div>
          </div>
        </div>
      ) : !webviewData ? (
        <div className='iframe-wrapper'>
          <div className='fake-url-bar'>
            <span className='url-text'>{url}</span>
          </div>
          <div className='webview-content'>
            <div className='error-message'>No content available</div>
          </div>
        </div>
      ) : (
        <div className='iframe-wrapper'>
          <div className='fake-url-bar'>
            <span className='url-text'>{webviewData.url}</span>
            {!webviewData.canUseIframe && webviewData.reason && (
              <span
                className='fallback-indicator'
                title={`Using screenshot: ${webviewData.reason}`}
              >
                📸
              </span>
            )}
          </div>

          <div className='webview-content'>
            {webviewData.type === 'iframe' ? (
              <iframe
                src={webviewData.url}
                className='webview-iframe'
                style={{ width: '100%', height: '100%' }}
                title='External content'
                sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
              />
            ) : (
              <div className='screenshot-container'>
                <img
                  src={webviewData.screenshotUrl}
                  alt='Website screenshot'
                  className='screenshot-image'
                  onError={(e) => {
                    console.error('Screenshot failed to load');
                    setError('Failed to load screenshot');
                  }}
                />
                <div className='screenshot-overlay'>
                  <a
                    href={webviewData.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='open-external-link'
                  >
                    Open in new tab ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comments positioned relative to the container */}
      {componentId && componentComments.length > 0 && (
        <Comments
          comments={componentComments}
          baseElementRef={containerRef}
          targetType='component'
          targetId={componentId}
        />
      )}
    </div>
  );
};

export default InlineWebview;
